import { NO_TEXT, type Translated } from '@myre/common-i18n';

import { type ResolvedMenuRoute } from '../interfaces';
import { getAbsolutePathForRoute } from '../routing/routing.helpers';

import { rootNodeName } from './menu.constants';
import {
  MenuBlockType,
  type ConsolidatedMenuItem,
  type MenuBlock,
} from './menu.interfaces';

export function routesToMenuBlocks(
  routes: readonly ResolvedMenuRoute[],
  baseUrl: string,
): MenuBlock[] {
  if (!routes) {
    return [];
  }

  const nodes = getNodesRoutesMap(routes, baseUrl);

  const menuBlocks: MenuBlock[] = [];

  for (const [key, { label, items }] of nodes) {
    if (!items.length) {
      continue;
    }

    if (key === rootNodeName) {
      menuBlocks.push({
        items,
        type: MenuBlockType.Secondary,
      });

      continue;
    }

    menuBlocks.push({
      type: MenuBlockType.Tertiary,
      items: [
        {
          label,
          path: null,
          absolutePath: null,
          expanded: false,
          children: items,
        },
      ],
    });
  }

  return menuBlocks;
}

/**
 * Groups the resolved menu entries by node: the entries without one under the root, the
 * others under their node key, each group carrying the label its header shows.
 *
 * @example
 * ```
 * [
 *   { path: 'menu-item-1', menuItem: { label } },
 *   { path: 'menu-sub-item-1', menuItem: { label, node: { key: 'node-1', label } } },
 * ];
 *
 * // Will return the following map:
 * {
 *    ['root': { label: NO_TEXT, items: [...consolidatedMenuItems] }],
 *    ['node-1': { label, items: [...consolidatedMenuItems] }],
 * }
 * ```
 */
export function getNodesRoutesMap(
  routes: readonly ResolvedMenuRoute[],
  baseUrl: string,
): Map<string, { label: Translated; items: ConsolidatedMenuItem[] }> {
  const nodes = new Map<
    string,
    { label: Translated; items: ConsolidatedMenuItem[] }
  >([[rootNodeName, { label: NO_TEXT, items: [] }]]);

  for (const route of routes || []) {
    const { menuItem } = route;

    if (!menuItem) {
      continue;
    }

    const key = menuItem.node?.key ?? rootNodeName;
    const group = nodes.get(key) ?? {
      label: menuItem.node?.label ?? NO_TEXT,
      items: [],
    };

    group.items.push(routeToConsolidatedMenuItems(route, baseUrl));
    nodes.set(key, group);
  }

  return nodes;
}

export function routeToConsolidatedMenuItems(
  route: ResolvedMenuRoute,
  baseUrl: string,
): ConsolidatedMenuItem | undefined {
  if (!route.menuItem) {
    return;
  }

  return {
    path: route.path,
    absolutePath: getAbsolutePathForRoute(route, baseUrl),
    alternateAbsolutePaths: route.menuItem.alternatePaths?.map(path =>
      getAbsolutePathForRoute({ path }, baseUrl),
    ),
    label: route.menuItem.label,
    icon: route.menuItem.icon,
    expanded: false,
    secondaryIcon: route.menuItem.secondaryIcon,
  };
}
